/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RecordDestinationsSection.module.scss';

type Translate = (key: string) => string;

type Destination = {
  key: string;
  name: string;
  icon: string;
  iconOnDark?: boolean;
  writes: string[];
  detail: string;
  href: string;
  linkLabel?: string;
};

const getDestinations = (t: Translate): Destination[] => [
  {
    key: 'hubspot',
    name: 'HubSpot',
    icon: 'crm-2',
    writes: [t('Company'), t('Contact'), t('Association')],
    detail: t(
      'Fields map to your HubSpot properties on the record page or in bulk across a list — enriched properties feed active workflows straight away.',
    ),
    href: '/hubspot-data-enrichment',
  },
  {
    key: 'salesforce',
    name: 'Salesforce',
    icon: 'crm-1',
    writes: [t('Account'), t('Contact')],
    detail: t(
      'Registration numbers give your dedupe rules an official identifier, and the filed group structure builds the Parent Account hierarchy.',
    ),
    href: '/salesforce-integration',
  },
  {
    key: 'dynamics',
    name: 'Microsoft Dynamics 365',
    icon: 'crm-3',
    writes: [t('Account'), t('Contact')],
    detail: t(
      'Registry-held legal names, registration numbers and VAT land on the fields finance depends on when the account flows into your ERP.',
    ),
    href: '/microsoft-dynamics-integration',
  },
  {
    key: 'list',
    name: 'Global Database list',
    icon: 'logo-3',
    writes: [t('Saved list'), t('Export'), t('Bulk enrich')],
    detail: t('No CRM connected yet? Keep the record in a shared list, then export it or bulk-enrich it later.'),
    href: '/data-dictionary',
    linkLabel: t('Field reference'),
  },
];

export const RecordDestinationsSection = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div ref={ref} className={cn(styles.lead, revealed && styles.in)}>
        <TitleBlock
          align="left"
          title={t('Write the company and its contacts straight into your CRM.')}
          description={t(
            'Records arrive as objects rather than text, with registry fields mapped to your properties. Matching runs on the registration number, so a parent and its subsidiaries stay as separate accounts instead of collapsing into one.',
          )}
        />
      </div>

      <p className={cn(styles.panelLabel, revealed && styles.in)}>{t('Writes to')}</p>

      <div className={styles.grid}>
        {getDestinations(t).map((destination, index) => (
          <a
            key={destination.key}
            href={destination.href}
            className={cn(styles.card, revealed && styles.in)}
            style={{ transitionDelay: revealed ? `${index * 0.08}s` : '0s' }}
          >
            <span className={cn(styles.cardIcon, destination.iconOnDark && styles.cardIconDark)}>
              <Icon type={destination.icon} size="big" />
            </span>

            <span className={styles.cardName}>{destination.name}</span>

            <span className={styles.chips}>
              {destination.writes.map((item) => (
                <span key={item} className={styles.chip}>
                  {item}
                </span>
              ))}
            </span>

            <span className={styles.cardDetail}>{destination.detail}</span>

            <span className={styles.cardLink}>
              {destination.linkLabel ?? t('Integration detail')}

              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M2.5 7h9M8 3.5 11.5 7 8 10.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </a>
        ))}
      </div>

      <div className={cn(styles.match, revealed && styles.in)}>
        <div className={styles.matchNote}>
          <span className={styles.matchKey}>registration_number</span>
          <p className={styles.matchText}>
            {t('Match runs on this field, so a parent and its subsidiaries write to')} <b>{t('separate')}</b>{' '}
            {t('accounts instead of merging into one.')}
          </p>
        </div>

        <div className={styles.diagram} aria-hidden="true">
          <span className={styles.node}>Meridian Holdings SA</span>

          <span className={styles.branch}>
            <span className={styles.node}>Meridian Logistics Ltd</span>
            <span className={styles.node}>Meridian Freight Services Ltd</span>
          </span>
        </div>
      </div>
    </Section>
  );
};
